import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useLang } from "@/i18n";
import { Reveal, FadeStagger, FadeItem } from "@/components/Motion";

const EASE = [0.22, 1, 0.36, 1];

const VISUAL_TYPES = {
  "aws-migration": "timeline",
  "aws-well-architected": "pillars",
  "aws-security": "controls",
  "databases-data-models": "beforeafter",
  "ga4-digital-analytics": "timeline",
  "crm-lifecycle": "beforeafter",
  "bigquery-dashboarding": "pillars",
  "advanced-modeling": "controls",
};

const Bar = ({ pct, color, delay = 0 }) => (
  <div className="h-2.5 w-full overflow-hidden rounded-full bg-black/[0.06]">
    <motion.div
      className="h-full rounded-full"
      style={{ backgroundColor: color }}
      initial={{ width: 0 }}
      whileInView={{ width: `${pct}%` }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    />
  </div>
);

const Timeline = ({ phases, accent }) => (
  <div className="relative">
    <motion.div
      className="absolute left-[7px] top-2 bottom-2 w-[2px] rounded-full origin-top"
      style={{ backgroundColor: `${accent}40` }}
      initial={{ scaleY: 0 }}
      whileInView={{ scaleY: 1 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 1.4, ease: EASE }}
    />
    <div className="flex flex-col gap-7">
      {phases.map((ph, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.6, delay: 0.15 + i * 0.18, ease: EASE }}
          className="relative flex items-start gap-5 pl-1"
        >
          <span className="relative z-10 mt-1 h-3.5 w-3.5 shrink-0 rounded-full border-[3px] bg-white" style={{ borderColor: accent }} />
          <div>
            <div className="font-cabinet text-base sm:text-lg font-bold">{ph.t}</div>
            <div className="mt-0.5 font-jbmono text-[10px] uppercase tracking-[0.2em]" style={{ color: accent }}>{ph.w}</div>
            <p className="mt-1.5 text-sm font-jakarta text-[#999999] leading-relaxed max-w-xl">{ph.d}</p>
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

const PillarBars = ({ pillars, note, accent }) => (
  <div className="flex flex-col gap-5">
    {pillars.map((pl, i) => (
      <div key={i}>
        <div className="mb-2 flex items-baseline justify-between gap-4">
          <span className="font-jakarta text-sm font-semibold">{pl.n}</span>
          <span className="font-jbmono text-xs" style={{ color: accent }}>{pl.v}</span>
        </div>
        <Bar pct={pl.v} color={accent} delay={i * 0.1} />
      </div>
    ))}
    <p className="mt-1 font-jakarta text-xs text-[#999999]">{note}</p>
  </div>
);

const SecurityControls = ({ controls, stat, statLabel, accent }) => (
  <div>
    <FadeStagger className="grid gap-3 sm:grid-cols-2">
      {controls.map((c, i) => (
        <FadeItem key={i} className="flex items-center gap-3 rounded-xl border border-black/10 bg-white px-4 py-3.5">
          <ShieldCheck size={18} className="shrink-0 text-[#0d9488]" />
          <span className="font-jakarta text-sm text-[#353535]/85">{c}</span>
        </FadeItem>
      ))}
    </FadeStagger>
    <Reveal className="mt-8 flex items-baseline gap-3">
      <span className="font-cabinet text-5xl sm:text-6xl font-bold tracking-tight" style={{ color: accent }}>{stat}</span>
      <span className="font-jbmono text-[11px] uppercase tracking-[0.2em] text-[#999999]">{statLabel}</span>
    </Reveal>
  </div>
);

const BeforeAfter = ({ metrics, legendBefore, legendAfter, note, accent }) => (
  <div className="flex flex-col gap-7">
    <div className="flex items-center gap-6 font-jbmono text-[10px] uppercase tracking-[0.2em] text-[#999999]">
      <span className="flex items-center gap-2"><span className="h-2 w-6 rounded-full bg-black/15" /> {legendBefore}</span>
      <span className="flex items-center gap-2"><span className="h-2 w-6 rounded-full" style={{ backgroundColor: accent }} /> {legendAfter}</span>
    </div>
    {metrics.map((mt, i) => (
      <div key={i}>
        <div className="mb-2.5 font-jakarta text-sm font-semibold">{mt.label}</div>
        <div className="flex items-center gap-3">
          <div className="flex-1"><Bar pct={mt.beforePct} color="rgba(53,53,53,0.25)" delay={i * 0.12} /></div>
          <span className="w-20 shrink-0 text-right font-jbmono text-xs text-[#999999] line-through decoration-black/30">{mt.before}</span>
        </div>
        <div className="mt-2 flex items-center gap-3">
          <div className="flex-1"><Bar pct={mt.afterPct} color={accent} delay={0.15 + i * 0.12} /></div>
          <span className="w-20 shrink-0 text-right font-jbmono text-xs font-bold" style={{ color: accent }}>{mt.after}</span>
        </div>
      </div>
    ))}
    <p className="font-jakarta text-xs text-[#999999]">{note}</p>
  </div>
);

const ServiceDeepDive = ({ service, accent = "#ff764a", unit = "aws" }) => {
  const { lang } = useLang();
  const d = service.deep?.[lang];
  if (!d) return null;

  const visualType = VISUAL_TYPES[service.slug];

  return (
    <section data-testid={`deep-${service.slug}`} className="px-5 sm:px-8 py-16 sm:py-24 border-t border-black/10 bg-white">
      <div className="mx-auto max-w-[1400px]">
        <Reveal>
          <div className="font-jbmono text-[11px] uppercase tracking-[0.3em] mb-4" style={{ color: accent }}>{d.kicker}</div>
          <h2 className="font-cabinet text-3xl sm:text-5xl font-bold tracking-tight leading-[1.08] max-w-3xl">{d.title}</h2>
          <p className="mt-5 max-w-2xl text-sm sm:text-base font-jakarta text-[#888888] leading-relaxed">{d.intro}</p>
        </Reveal>

        <FadeStagger className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" delay={0.05}>
          {d.caps.map((cap, i) => (
            <FadeItem key={i} data-testid={`deep-${service.slug}-cap-${i}`} className="rounded-2xl border border-black/10 bg-[#FAFAFD] p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(53,53,53,0.08)]">
              <div className="font-jbmono text-[10px] uppercase tracking-[0.2em]" style={{ color: accent }}>{String(i + 1).padStart(2, "0")}</div>
              <h3 className="mt-3 font-cabinet text-lg font-bold leading-snug">{cap.t}</h3>
              <p className="mt-2 text-[13px] font-jakarta text-[#999999] leading-relaxed">{cap.d}</p>
            </FadeItem>
          ))}
        </FadeStagger>

        <div className="mt-16 grid gap-12 lg:grid-cols-2 items-start">
          <Reveal>
            <div className="font-jbmono text-[11px] uppercase tracking-[0.3em] text-[#999999] mb-8">{d.visual.label}</div>
            {visualType === "timeline" && <Timeline phases={d.visual.phases} accent={accent} />}
            {visualType === "pillars" && <PillarBars pillars={d.visual.pillars} note={d.visual.note} accent={accent} />}
            {visualType === "controls" && <SecurityControls controls={d.visual.controls} stat={d.visual.stat} statLabel={d.visual.statLabel} accent={accent} />}
            {visualType === "beforeafter" && <BeforeAfter metrics={d.visual.metrics} legendBefore={d.visual.legendBefore} legendAfter={d.visual.legendAfter} note={d.visual.note} accent={accent} />}
          </Reveal>
          <div className="flex flex-col gap-0">
            {d.steps.map((s, i) => (
              <Reveal key={i} delay={0.08 * i} className="flex gap-5 border-t border-black/10 py-5 first:border-t-0 first:pt-0">
                <span className="font-jbmono text-xs pt-1 shrink-0" style={{ color: accent }}>{s.n}</span>
                <div>
                  <div className="font-cabinet text-base font-bold">{s.t}</div>
                  <p className="mt-1 text-[13px] font-jakarta text-[#999999] leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal className="mt-16 rounded-2xl bg-[#353535] px-7 py-10 sm:px-12 sm:py-14 text-white">
          <div className="flex flex-wrap items-center justify-between gap-8">
            <div className="max-w-xl">
              <h3 className="font-cabinet text-2xl sm:text-4xl font-bold tracking-tight leading-tight">{d.closing.title}</h3>
              <p className="mt-3 text-sm sm:text-base font-jakarta text-white/60 leading-relaxed">{d.closing.body}</p>
            </div>
            <Link
              to={`/contact?unit=${unit}&service=${service.slug}`}
              data-testid={`deep-${service.slug}-cta`}
              className="group inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-jakarta font-bold text-white transition-all duration-300 hover:gap-4"
              style={{ backgroundColor: accent }}
            >
              {d.closing.cta} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default ServiceDeepDive;
